/**
 * Local-dev-only Supabase READ switch.
 *
 * Lets you start the app in local development and have all data reads come from
 * Supabase (instead of the default Firestore) without touching any per-org or
 * per-device production state. It is intended purely for testing the Supabase
 * read path locally.
 *
 * Safety: this is DOUBLE-GATED so it can never fire in a production build:
 *   1. `isLocalDev` — true only in a dev build (`__DEV__`), false in every
 *      release/production bundle.
 *   2. `EXPO_PUBLIC_SUPABASE_READS === 'true'` — an explicit opt-in env var that
 *      is only ever set in your local `.env.local` (gitignored) and is never
 *      present in EAS production env.
 *
 * Because both conditions must hold, this branch is dead code in production —
 * `shouldUseSupabase()` keeps its existing behavior there byte-for-byte.
 *
 * To turn it on locally: add `EXPO_PUBLIC_SUPABASE_READS=true` to
 * `apps/atlas/.env.local`, then `npm run dev:atlas`.
 */

// True only in development builds. `__DEV__` is a React Native / Expo global that
// the bundler replaces with `false` for production. Guard the typeof for runtimes
// (e.g. some web/test contexts) where it may be undefined, mirroring the
// `isProductionBuild()` pattern in global/Firebase/dualWrite.ts.
const isLocalDev = ((): boolean => {
  try {
    if (typeof __DEV__ !== 'undefined') return __DEV__
  } catch {
    /* __DEV__ not defined in this runtime */
  }
  return process.env.NODE_ENV !== 'production'
})()

/**
 * Whether the local-dev Supabase read override is active.
 *
 * @returns true only when running a dev build AND `EXPO_PUBLIC_SUPABASE_READS=true`.
 */
export const localDevSupabaseReads = (): boolean =>
  isLocalDev && process.env.EXPO_PUBLIC_SUPABASE_READS === 'true'
